import type { AttestationNotRequiredMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

/**
 * Maps the tRPC mutation response to the GraphQL mutation response shape so
 * consumers branching on the result do not need to know which path executed.
 */
const mapTrpcResponseToGraphQL = (data: {
  affected_rows: number;
}): AttestationNotRequiredMutation => ({
  attestationNotRequired: {
    __typename: 'GenericMutationOutput',
    affected_rows: data.affected_rows,
  },
});

export const useMarkAttestationsNotRequiredTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.attestation.notRequired.mutationOptions({
      onSuccess: async () => {
        // Parity with the Apollo path: the register feeds the page the action is
        // triggered from, and the per-document status drives the attestation
        // widgets. Both go stale once records are deactivated.
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.attestation.register.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.attestation.status.queryKey(),
          }),
        ]);
      },
    }),
  });

  return {
    markNotRequired: async (input: {
      Ids: string[];
    }): Promise<AttestationNotRequiredMutation> => {
      const result = await mutation.mutateAsync({ Ids: input.Ids });

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
